import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { obtenerReporteAnses } from '../../services/reportes/reportesAnsesService';
import KpiRow from '../Recepcion/reportes/KpiRow';
import BarraPeriodo from '../Recepcion/reportes/BarraPeriodo';
import { usePeriodoReportes } from '../Recepcion/reportes/periodoReportes';
import { COLOR, FUENTE, UMBRALES } from '../Recepcion/reportes/tokens';
import PresentacionesPorTipoChart from './reportes/PresentacionesPorTipoChart';
import AgendadosPorTipoChart from './reportes/AgendadosPorTipoChart';
import EvolucionMensualChart from './reportes/EvolucionMensualChart';
import ResultadosDistribucion from './reportes/ResultadosDistribucion';
import MotivosRechazoLista from './reportes/MotivosRechazoLista';
import TablaUdai from './reportes/TablaUdai';

/**
 * Los períodos que el estudio mira de verdad en ANSES. No son los de Recepción: acá el ciclo es
 * largo (se presenta un mes y ANSES contesta meses después), así que "Hoy" o "Últimos 7 días" no
 * dirían nada, y en cambio los cortes de 3 y 6 meses sí.
 *
 * El interruptor "Incluir turnos futuros" de BarraPeriodo aplica a todos por igual: por defecto
 * el período se corta en hoy, así la agenda que todavía no ocurrió deja de contar como trabajo
 * del período (eran 35 de los 120 turnos de un mes en curso típico).
 */
const RANGOS_ANSES = [
    { id: 'mes', label: 'Mes' },
    { id: 'trimestre', label: 'Últimos 3 meses' },
    { id: 'semestre', label: 'Últimos 6 meses' },
    { id: 'anio', label: 'Año' },
    { id: 'rango', label: 'Rango' },
];

/**
 * Reportes de turnos ANSES: cuánto se presenta de cada cosa, cómo sale y qué se pierde.
 *
 * Sigue el sistema de diseño de Reportes Recepción (tokens, chartBase, KpiRow) y ahora también
 * su manejo de período (usePeriodoReportes + BarraPeriodo), para que los reportes del sistema se
 * lean como uno solo y no como pantallas sueltas.
 *
 * Estos reportes no existían porque no había qué contar: los turnos migrados no tenían
 * presentación, y sin presentación no hay tipo de trámite ni resultado.
 */
function ReportesAnsesPage() {
    const periodo = usePeriodoReportes('semestre', RANGOS_ANSES);
    const { desde, hasta, vacio } = periodo;
    const [reporte, setReporte] = useState(null);
    const [cargando, setCargando] = useState(true);

    const cargar = useCallback(async () => {
        if (vacio) {
            setReporte(null);
            setCargando(false);
            return;
        }
        setCargando(true);
        try {
            setReporte(await obtenerReporteAnses({ desde: desde || null, hasta: hasta || null }));
        } catch (e) {
            toast.error(e?.response?.data?.mensaje || 'No se pudo cargar el reporte.');
            setReporte(null);
        } finally {
            setCargando(false);
        }
    }, [desde, hasta, vacio]);

    useEffect(() => { cargar(); }, [cargar]);

    const kpis = useMemo(() => {
        const r = reporte?.resumen;
        if (!r) return [];
        const sinTasa = r.tasaIngreso === null || r.tasaIngreso === undefined;
        // Los "programados" incluyen idas que ya pasaron y nadie cerró: decir que están "por
        // delante" era falso para casi la mitad de ellas.
        const agendados = Math.max(0, (r.programados || 0) - (r.turnosPasadosSinCerrar || 0));
        const partesTurnos = [`${r.realizados} ya ocurrieron`, `${agendados} agendados`];
        if (r.cancelados > 0) partesTurnos.push(`${r.cancelados} cancelados`);
        // Vistas y citaciones no son idas a presentar: no entran en ningún número del reporte,
        // se informan aparte solo como dato.
        const sinPresentacion = [
            r.vistas > 0 && `${r.vistas} ${r.vistas === 1 ? 'vista' : 'vistas'}`,
            r.citaciones > 0 && `${r.citaciones} ${r.citaciones === 1 ? 'citación' : 'citaciones'}`,
        ].filter(Boolean);
        if (sinPresentacion.length > 0) partesTurnos.push(`aparte: ${sinPresentacion.join(' y ')}`);

        return [
            {
                label: 'Turnos en ANSES',
                value: r.totalTurnos,
                hint: partesTurnos.join(' · '),
            },
            {
                label: 'Presentaciones',
                value: r.totalPresentaciones,
                hint: `${r.pendientes} sin resultado registrado`,
            },
            {
                label: 'Tasa de ingreso',
                // null cuando la muestra es chica: se muestra "—", no un número que engañe.
                value: sinTasa ? '—' : r.tasaIngreso,
                unit: sinTasa ? '' : '%',
                hint: sinTasa
                    ? `Menos de ${UMBRALES.muestraMinimaAnses} presentaciones resueltas`
                    : `${r.entraronAAnses} de ${r.totalPresentaciones - r.pendientes} resueltas entraron`,
            },
            {
                // Lo único del reporte que señala trabajo pendiente: como las tasas se calculan
                // sobre lo resuelto, estas presentaciones no aparecen en ningún otro número.
                label: 'Sin registrar',
                value: r.presentacionesPendientesVencidas,
                isGood: (r.presentacionesPendientesVencidas || 0) === 0,
                hint: r.turnosPasadosSinCerrar > 0
                    ? `De ${r.turnosPasadosSinCerrar} idas que ya pasaron y siguen abiertas`
                    : 'Presentaciones de idas que ya ocurrieron',
            },
            {
                label: 'Firmas pendientes',
                value: r.firmasPendientes,
                hint: 'Hay que resolverlas antes del turno',
            },
        ];
    }, [reporte]);

    return (
        <Box sx={{ padding: '24px 28px', background: '#FAFBFC', minHeight: '100vh' }}>
            <ToastContainer position="top-right" autoClose={4000} />

            <Box sx={{ marginBottom: '18px' }}>
                <Typography sx={{ fontFamily: FUENTE, fontSize: 22, fontWeight: 700, color: COLOR.negro }}>
                    Reportes de turnos ANSES
                </Typography>
                <Typography sx={{ fontFamily: FUENTE, fontSize: 13, color: COLOR.grisTexto, marginTop: '2px' }}>
                    Qué se presenta ante el organismo, cómo sale y qué se pierde en el camino.
                </Typography>
            </Box>

            <BarraPeriodo periodo={periodo} rangos={RANGOS_ANSES} />

            {cargando ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <Skeleton variant="rounded" height={96} />
                    <Skeleton variant="rounded" height={300} />
                    <Skeleton variant="rounded" height={280} />
                </Box>
            ) : !reporte ? (
                <Typography sx={{ fontFamily: FUENTE, fontSize: 14, color: COLOR.grisFrio, padding: '40px 0', textAlign: 'center' }}>
                    {/* Con el período vacío no hubo error: BarraPeriodo ya explica por qué y cómo verlo. */}
                    {vacio ? 'El período elegido no incluye ningún día todavía.' : 'No se pudo cargar el reporte.'}
                </Typography>
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '18px',
                        // Tope de ancho en los hijos: sin esto las tarjetas con gráfico tomaban el ancho
                        // del canvas (300 px) en vez del de la columna (283 px en un celular de 375) y la
                        // página se ensanchaba. Medido en el navegador: con maxWidth 100% quedan en 283.
                        '& > *': { minWidth: 0, maxWidth: '100%' },
                    }}
                >
                    <KpiRow items={kpis} />

                    <ResultadosDistribucion data={reporte.porResultado} />
                    <PresentacionesPorTipoChart data={reporte.porTipo} />
                    <AgendadosPorTipoChart data={reporte.agendadosPorTipo}
                        desde={reporte.agendadosDesde} hasta={reporte.agendadosHasta} />
                    <EvolucionMensualChart data={reporte.porMes} />

                    {/* Los dos cortes que dicen dónde y por qué se traba, uno al lado del otro. */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: '18px' }}>
                        <MotivosRechazoLista data={reporte.motivosRechazo} />
                        <TablaUdai data={reporte.porUdai} />
                    </Box>
                </Box>
            )}
        </Box>
    );
}

export default ReportesAnsesPage;
